<template>
    <div class="school-detail">
        <!-- 学校图片 -->
        <div class="school-image-container" v-if="schoolMainPic">
            <img :src="schoolMainPic" alt="学校图片" class="school-image">
        </div>

        <!-- 学校基本信息 -->
        <div class="school-info-section" v-if="schoolInfo.remark">
            <div class="school-logo" v-if="schoolLogo">
                <img :src="schoolLogo" alt="学校 logo">
            </div>
            <div class="school-basic-info">
                <h2 class="school-name">{{ schoolInfo.name }}</h2>
                <div class="school-meta">
                    <div class="meta-row">{{ schoolInfo.districtName }} | {{ schoolInfo.typeName }}</div>
                </div>
            </div>
            <div class="nav-btn" @click="navigateToMap">导航</div>
        </div>

        <!-- 学校简介 -->
        <div class="section" v-if="schoolInfo.remark">
            <div class="section-header">
                <div class="section-icon info-icon">
                    <van-icon name="comment" size="18" color="#409EFF" />
                </div>
                <h3 class="section-title">学校简介</h3>
            </div>
            <div class="section-content">
                <p>{{ schoolInfo.remark }}</p>
            </div>
        </div>

        <!-- 历史分数线 -->
        <div class="section" v-if="schoolInfo.scoreDetails && schoolInfo.scoreDetails.length > 0">
            <div class="section-header">
                <div class="section-icon chart-icon">
                    <van-icon name="chart-trending-o" size="18" color="#409EFF" />
                </div>
                <h3 class="section-title">历史分数线</h3>
            </div>
            <div class="section-content">
                <div class="score-table">
                    <div class="table-header">
                        <div class="header-item">年份</div>
                        <div class="header-item">AC类(分数/人数)</div>
                        <div class="header-item">D类(分数/人数)</div>
                    </div>
                    <template v-for="item in schoolInfo.scoreDetails" :key="item.id">
                        <div class="table-row" v-if="item.acAdmissionScore">
                            <div class="table-cell"><strong>{{ item.examYear }}</strong></div>
                            <div class="table-cell">{{ item.acAdmissionScore }}/{{ item.acRecruitNum }}</div>
                            <div class="table-cell">{{ item.dAdmissionScore }}/{{ item.dRecruitNum }}</div>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!-- 校长介绍 -->
        <div class="section" v-if="schoolInfo.principalIntro">
            <div class="section-header">
                <div class="section-icon person-icon">
                    <van-icon name="manager" size="18" color="#409EFF" />
                </div>
                <h3 class="section-title">校长介绍</h3>
            </div>
            <div class="section-content">
                <div class="content-box">
                    <p>{{ schoolInfo.principalIntro }}</p>
                </div>
            </div>
        </div>

        <!-- 学校特色 -->
        <template v-for="feature in filteredFeatures" :key="feature.id">
            <div class="section">
                <div class="section-header">
                    <div class="section-icon">
                        <van-icon :name="feature.icon" size="18" color="#409EFF" />
                    </div>
                    <h3 class="section-title">{{ feature.featureName }}</h3>
                </div>
                <div class="section-content">
                    <div class="content-box">
                        <p>{{ feature.featureValue }}</p>
                    </div>
                </div>
            </div>
        </template>

        <!-- 交通情况 -->
        <div class="section" v-if="schoolInfo.trafficGuide">
            <div class="section-header">
                <div class="section-icon transport-icon">
                    <van-icon name="location-o" size="18" color="#409EFF" />
                </div>
                <h3 class="section-title">交通情况</h3>
            </div>
            <div class="section-content">
                <div class="content-box">
                    <p>{{ schoolInfo.trafficGuide }}</p>
                </div>
            </div>
        </div>


    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { getSchoolCompleteInfo } from '@/api/report'
import { getWxJsapiSignature } from '@/api/auth'

const router = useRouter()
const schoolInfo = ref({})
const featureConfig = [
  { name: '学校特色', icon: 'flag-o' },
  { name: '分班依据', icon: 'wap-home-o' },
  { name: '食堂品质', icon: 'shop-o' },
  { name: '班型设置', icon: 'cluster-o' },
  { name: '手机管理', icon: 'phone-o' },
  { name: '师资情况', icon: 'user-o' },
  { name: '宿舍详情', icon: 'hotel-o' },
  { name: '宿舍规模', icon: 'friends-o' }
]

const schoolLogo = computed(() => {
  if (schoolInfo.value.logo) return schoolInfo.value.logo.replace(/`/g, '').trim()
  if (schoolInfo.value.logoUrl) return schoolInfo.value.logoUrl
  return ''
})
const schoolMainPic = computed(() => {
  if (schoolInfo.value.mainPics) {
    const pics = schoolInfo.value.mainPics.replace(/`/g, '').trim().split(',')
    return pics[0] || ''
  }
  return ''
})
const filteredFeatures = computed(() => {
  if (!schoolInfo.value.features || !Array.isArray(schoolInfo.value.features)) return []
  return schoolInfo.value.features
    .filter(f => f.featureValue)
    .sort((a, b) => {
      const indexA = featureConfig.findIndex(c => c.name === a.featureName)
      const indexB = featureConfig.findIndex(c => c.name === b.featureName)
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB)
    })
    .map(f => ({ ...f, icon: featureConfig.find(c => c.name === f.featureName)?.icon || 'info-o' }))
})

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search)
  const schoolId = urlParams.get('schoolId')
  if (schoolId) {
    getSchoolCompleteInfo(schoolId).then(res => {
      schoolInfo.value = res
    })
  }
})

function goBack() {
  router.back()
}

function navigateToMap() {
  getWxJsapiSignature({ url: window.location.href }).then(res => {
    wx.config({
      debug: false,
      appId: res.appId,
      timestamp: res.timestamp,
      nonceStr: res.nonceStr,
      signature: res.signature,
      jsApiList: ['openLocation']
    })
    wx.ready(() => {
      const latitude = Number(schoolInfo.value.latitude)
      const longitude = Number(schoolInfo.value.longitude)
      wx.openLocation({
        latitude,
        longitude,
        name: schoolInfo.value.name,
        address: schoolInfo.value.districtName,
        scale: 15,
        infoUrl: '',
        success: () => {},
        fail: () => {}
      })
    })
  })
}
</script>

<style scoped>
/* 全局样式 */
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

.school-detail {
    font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
    background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
    min-height: 100vh;
    color: #333;
    padding-bottom: 20px;
}

/* 导航栏 */
.navbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 44px;
    background-color: #ffffff;
    padding: 0 16px;
    border-bottom: 1px solid #f0f0f0;
    position: sticky;
    top: 0;
    z-index: 100;
}

.back-btn,
.share-btn {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.navbar-title {
    font-size: 17px;
    font-weight: 500;
    color: #333;
    text-decoration: underline;
    text-underline-offset: 4px;
}

/* 学校图片 */
.school-image-container {
    width: calc(100% - 32px);
    margin: 16px;
    border-radius: 12px;
    overflow: hidden;
}

.school-image {
    width: 100%;
    height: 200px;
    object-fit: cover;
    display: block;
}

/* 学校基本信息 */
.school-info-section {
    display: flex;
    padding: 0 16px 20px;
    background-color: transparent;
}

.school-logo {
    width: 64px;
    height: 64px;
    border-radius: 12px;
    overflow: hidden;
    margin-right: 16px;
    flex-shrink: 0;
    background-color: #1a5f5f;
}

.school-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.school-basic-info {
    flex: 1;
    min-width: 0;
}

.school-name {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
    line-height: 1.4;
}

.school-meta {
    font-size: 14px;
    color: #999;
    line-height: 1.6;
}

.meta-row {
    margin-bottom: 2px;
}

.nav-btn {
    background-color: #e6f0ff;
    color: #409EFF;
    font-size: 14px;
    padding: 6px 12px;
    border-radius: 4px;
    height: fit-content;
    align-self: center;
}

/* 通用 section 样式 */
.section {
    padding: 16px;
    margin-bottom: 12px;
}

.section-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
}

.section-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
}

.section-title {
    font-size: 17px;
    font-weight: 500;
    color: #333;
}

.section-content {
    font-size: 15px;
    line-height: 1.8;
    color: #666;
}

.section-content p {
    margin: 0;
}

/* 内容框样式 */
.content-box {
    background-color: #f0f4ff;
    border-radius: 12px;
    padding: 16px;
}

.content-box p {
    margin: 0;
}

/* 分数线表格 */
.score-table {
    width: 100%;
    background-color: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.table-header {
    display: flex;
    background-color: #f8f9fa;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
}

.header-item {
    flex: 1;
    font-size: 14px;
    color: #999;
    text-align: left;
}

.table-row {
    display: flex;
    padding: 16px;
    border-bottom: 1px solid #f5f5f5;
}

.table-row:last-child {
    border-bottom: none;
}

.table-cell {
    flex: 1;
    font-size: 15px;
    color: #333;
    text-align: left;
}

.table-cell strong {
    font-weight: 600;
}

.table-cell.score {
    color: #409EFF;
    font-weight: 500;
}

/* 响应式调整 */
@media (max-width: 375px) {
    .school-name {
        font-size: 17px;
    }

    .section-title {
        font-size: 16px;
    }

    .section-content {
        font-size: 14px;
    }
}
</style>